import { useState, type FormEvent } from "react";
import { CONTEXTS } from "./format";
import type { ClarifyAction, ClarifyDecision, Energy, TaskStatus } from "./types";

/** Limit kolumny tytułu; dłuższa wrzutka idzie do notatki, a tytuł robi z niej serwer. */
const TITLE_MAX_LENGTH = 512;

const ACTIONS: { value: ClarifyAction; label: string; hint: string }[] = [
  { value: "task", label: "Zadanie", hint: "Jedna konkretna akcja" },
  { value: "project", label: "Projekt", hint: "Więcej niż jeden krok" },
  { value: "two_minutes", label: "2 minuty", hint: "Zrobione od razu" },
  { value: "someday", label: "Kiedyś", hint: "Może kiedyś / lista życzeń" },
  { value: "reference", label: "Referencja", hint: "Materiał do wglądu albo pozycja listy" },
  { value: "trash", label: "Kosz", hint: "Nic z tym nie robimy" },
];

interface Props {
  rawText: string;
  busy?: boolean;
  onSubmit: (decision: ClarifyDecision) => void;
  onCancel: () => void;
}

/** Manual clarify (no LLM): pick a verdict, fill the fields it needs. */
export function ClarifyForm({ rawText, busy, onSubmit, onCancel }: Props) {
  const [action, setAction] = useState<ClarifyAction>("task");
  const tooLongForTitle = rawText.trim().length > TITLE_MAX_LENGTH;
  const [title, setTitle] = useState(tooLongForTitle ? "" : rawText);
  const [note, setNote] = useState("");
  const [context, setContext] = useState("");
  const [status, setStatus] = useState<TaskStatus>("next");
  const [minutes, setMinutes] = useState("");
  const [energy, setEnergy] = useState<"" | Energy>("");
  const [ticklerDate, setTicklerDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [delegatedTo, setDelegatedTo] = useState("");
  const [outcome, setOutcome] = useState("");
  const [firstAction, setFirstAction] = useState("");
  const [checklist, setChecklist] = useState("");
  const [topic, setTopic] = useState("");

  const isTaskish = action === "task" || action === "someday" || action === "two_minutes";

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const decision: ClarifyDecision = { action };

    // Niezmieniony tytuł pomijamy: długa wrzutka nie zmieści się w tytule,
    // a serwer weźmie go z pierwszej linii i zachowa cały tekst.
    if (action !== "trash" && title.trim() && title.trim() !== rawText.trim()) {
      decision.title = title.trim();
    }
    if (note.trim()) decision.note = note.trim();

    if (isTaskish) {
      if (context) decision.context = context;
      if (minutes) decision.time_estimate_min = Number(minutes);
      if (energy) decision.energy = energy;
      if (dueDate && action !== "two_minutes") decision.due_date = dueDate;
      if (action === "task") {
        decision.status = status;
        if (status === "waiting") decision.delegated_to = delegatedTo.trim();
        if (status === "scheduled") decision.tickler_date = ticklerDate;
      }
    }

    if (action === "project") {
      if (outcome.trim()) decision.outcome = outcome.trim();
      decision.first_action = firstAction.trim();
      if (context) decision.context = context;
      if (dueDate) decision.due_date = dueDate;
    }

    if (action === "reference") {
      if (checklist.trim()) decision.checklist = checklist.trim();
      else if (topic.trim()) decision.topic = topic.trim();
    }

    onSubmit(decision);
  }

  const invalid =
    (action === "project" && !firstAction.trim()) ||
    (action === "task" && status === "waiting" && !delegatedTo.trim()) ||
    (action === "task" && status === "scheduled" && !ticklerDate);

  return (
    <form className="clarify" onSubmit={handleSubmit}>
      <div className="clarify__actions" role="radiogroup" aria-label="Typ decyzji">
        {ACTIONS.map((a) => (
          <button
            key={a.value}
            type="button"
            role="radio"
            aria-checked={action === a.value}
            title={a.hint}
            className={`chip ${action === a.value ? "chip--active" : ""}`}
            onClick={() => setAction(a.value)}
          >
            {a.label}
          </button>
        ))}
      </div>

      {action !== "trash" && (
        <label className="field">
          <span className="field__label">
            {action === "project" ? "Nazwa projektu" : "Tytuł"}
          </span>
          <input
            className="field__input"
            value={title}
            placeholder={tooLongForTitle ? "Puste = pierwsza linia wrzutki" : undefined}
            onChange={(e) => setTitle(e.target.value)}
          />
          {tooLongForTitle && (
            <span className="muted">
              {action === "reference" || action === "project"
                ? "Wrzutka jest za długa na tytuł; pełny tekst zostanie zachowany."
                : "Wrzutka jest za długa na tytuł; pełny tekst trafi do notatki."}
            </span>
          )}
        </label>
      )}

      {action === "project" && (
        <>
          <label className="field">
            <span className="field__label">Rezultat (jak wygląda ukończone?)</span>
            <input
              className="field__input"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">Pierwsze next action *</span>
            <input
              className="field__input"
              value={firstAction}
              onChange={(e) => setFirstAction(e.target.value)}
              placeholder="Konkretny fizyczny krok"
              required
            />
          </label>
        </>
      )}

      {action === "reference" && (
        <>
          {!checklist.trim() && (
            <label className="field">
              <span className="field__label">Temat</span>
              <input
                className="field__input"
                value={topic}
                maxLength={128}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="np. Dom, Finanse"
              />
            </label>
          )}
          <label className="field">
            <span className="field__label">Checklista (puste = materiał referencyjny)</span>
            <input
              className="field__input"
              value={checklist}
              onChange={(e) => setChecklist(e.target.value)}
              placeholder="np. Zakupy"
            />
          </label>
        </>
      )}

      {action === "task" && (
        <label className="field">
          <span className="field__label">Status</span>
          <select
            className="field__input"
            value={status}
            onChange={(e) => setStatus(e.target.value as TaskStatus)}
          >
            <option value="next">next — do zrobienia</option>
            <option value="waiting">waiting — czekam na kogoś</option>
            <option value="scheduled">scheduled — wróć do tego później</option>
          </select>
        </label>
      )}

      {action === "task" && status === "waiting" && (
        <label className="field">
          <span className="field__label">Na kogo czekamy? *</span>
          <input
            className="field__input"
            value={delegatedTo}
            onChange={(e) => setDelegatedTo(e.target.value)}
            required
          />
        </label>
      )}

      {action === "task" && status === "scheduled" && (
        <label className="field">
          <span className="field__label">Nie pokazuj przed *</span>
          <input
            className="field__input"
            type="date"
            value={ticklerDate}
            onChange={(e) => setTicklerDate(e.target.value)}
            required
          />
        </label>
      )}

      {(action === "task" || action === "someday" || action === "project") && (
        <label className="field">
          <span className="field__label">
            {action === "project"
              ? "Termin pierwszego kroku (musi być zrobione do)"
              : "Termin (musi być zrobione do)"}
          </span>
          <input
            className="field__input"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
      )}

      {(isTaskish || action === "project") && (
        <div className="field-row">
          <label className="field">
            <span className="field__label">Kontekst</span>
            <select
              className="field__input"
              value={context}
              onChange={(e) => setContext(e.target.value)}
            >
              <option value="">—</option>
              {CONTEXTS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Minuty</span>
            <input
              className="field__input"
              type="number"
              min="0"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">Energia</span>
            <select
              className="field__input"
              value={energy}
              onChange={(e) => setEnergy(e.target.value as "" | Energy)}
            >
              <option value="">—</option>
              <option value="low">low</option>
              <option value="med">med</option>
              <option value="high">high</option>
            </select>
          </label>
        </div>
      )}

      {action !== "trash" && action !== "project" && (
        <label className="field">
          <span className="field__label">Notatka</span>
          <input className="field__input" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      )}

      <div className="clarify__buttons">
        <button className="btn btn--primary" type="submit" disabled={busy || invalid}>
          {busy ? "Zapisuję…" : "Zatwierdź"}
        </button>
        <button className="btn" type="button" onClick={onCancel}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
